import React, { useEffect, useMemo, useState } from "react";
import {
  Backdrop,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  CssBaseline,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";

import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import DetailsPageHeader from "../../../../../common/DetailsPageHeader";
import { mainLayoutStyles, showNotification } from "../../../../../constants";
import ContactSubmissionModal from "../../components/ContactSubmissionModal/ContactSubmissionModal";
import { downloadSubmissionsCsv } from "../../utils/csv";
import {
  bulkUpdateSubmissionStatus,
  getContactSubmissions,
  submissionMillis,
  updateSubmissionStatus,
} from "../../hooks/ContactSubmissionsHook";

// Sortable columns: key -> accessor used for comparison.
const SORT_ACCESSORS = {
  createdAt: (r) => submissionMillis(r.createdAt),
  firstName: (r) => (r.firstName || "").toLowerCase(),
  lastName: (r) => (r.lastName || "").toLowerCase(),
  organization: (r) => (r.organization || "").toLowerCase(),
};

const ContactSubmissionsContainer = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [orderDir, setOrderDir] = useState("desc");
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeRow, setActiveRow] = useState(null);

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const data = await getContactSubmissions();
      setRows(data);
      setSelectedIds([]);
    } catch (error) {
      console.log("Error loading submissions:", error.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter (search) then sort.
  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = rows;
    if (term) {
      list = rows.filter((r) =>
        [
          r.firstName,
          r.lastName,
          r.email,
          r.phone,
          r.organization,
          r.message,
          r.formName,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term))
      );
    }
    const accessor = SORT_ACCESSORS[orderBy] || SORT_ACCESSORS.createdAt;
    const sorted = [...list].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return orderDir === "asc" ? -1 : 1;
      if (av > bv) return orderDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [rows, search, orderBy, orderDir]);

  const handleSort = (key) => {
    if (orderBy === key) {
      setOrderDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(key);
      setOrderDir(key === "createdAt" ? "desc" : "asc");
    }
  };

  const visibleIds = visibleRows.map((r) => r.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected =
    visibleIds.some((id) => selectedIds.includes(id)) && !allVisibleSelected;

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...visibleIds])]);
    }
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleOpenRow = async (row) => {
    setActiveRow(row);
    if ((row.status || "new") === "new") {
      try {
        await updateSubmissionStatus(row.id, "read");
        setRows((prev) =>
          prev.map((r) => (r.id === row.id ? { ...r, status: "read" } : r))
        );
        setActiveRow((r) => (r ? { ...r, status: "read" } : r));
      } catch (error) {
        console.log("Error marking read:", error.message);
      }
    }
  };

  const handleBulkStatus = async (status) => {
    if (selectedIds.length === 0) return;
    try {
      setLoading(true);
      await bulkUpdateSubmissionStatus(selectedIds, status);
      showNotification(`Marked ${selectedIds.length} as ${status}`);
      await loadSubmissions();
    } catch (error) {
      console.log("Error updating statuses:", error.message);
      showNotification("Error updating statuses");
      setLoading(false);
    }
  };

  const handleExport = () => {
    const chosen = rows.filter((r) => selectedIds.includes(r.id));
    if (chosen.length === 0) return;
    downloadSubmissionsCsv(chosen);
  };

  const formatDate = (ts) => {
    const ms = submissionMillis(ts);
    return ms ? new Date(ms).toLocaleString() : "—";
  };

  const sortLabel = (key, label) => (
    <TableSortLabel
      active={orderBy === key}
      direction={orderBy === key ? orderDir : "asc"}
      onClick={() => handleSort(key)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle="QuickTales.com — Contact Submissions"
        bgColor="#fff"
        hideBackArrow
      />
      <GeneralAdminSidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid
          container
          direction="column"
          style={{ padding: 30, marginTop: 40 }}
        >
          {/* Toolbar: search + selection actions */}
          <Grid container alignItems="center" sx={{ mb: 2 }}>
            <TextField
              variant="outlined"
              placeholder="Search name, email, phone, organization"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                sx: searchStyles,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon />
                  </InputAdornment>
                ),
              }}
            />
            {selectedIds.length > 0 && (
              <>
                <span style={selectedTextStyles}>
                  {selectedIds.length} selected
                </span>
                <span style={actionTextStyles} onClick={handleExport}>
                  Export CSV
                </span>
                <Divider
                  orientation="vertical"
                  style={{ height: 20, borderColor: "#919191" }}
                />
                <span
                  style={actionTextStyles}
                  onClick={() => handleBulkStatus("read")}
                >
                  Mark Read
                </span>
                <Divider
                  orientation="vertical"
                  style={{ height: 20, borderColor: "#919191" }}
                />
                <span
                  style={actionTextStyles}
                  onClick={() => handleBulkStatus("new")}
                >
                  Mark New
                </span>
              </>
            )}
          </Grid>

          <Paper sx={{ p: 1 }}>
            {visibleRows.length === 0 && !loading ? (
              <Typography sx={emptyStyles}>No contact submissions.</Typography>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allVisibleSelected}
                        indeterminate={someVisibleSelected}
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={headCellStyles}>Status</TableCell>
                    <TableCell sx={headCellStyles}>
                      {sortLabel("organization", "Organization")}
                    </TableCell>
                    <TableCell sx={headCellStyles}>
                      {sortLabel("firstName", "First Name")}
                    </TableCell>
                    <TableCell sx={headCellStyles}>
                      {sortLabel("lastName", "Last Name")}
                    </TableCell>
                    <TableCell sx={headCellStyles}>Email</TableCell>
                    <TableCell sx={headCellStyles}>Phone</TableCell>
                    <TableCell sx={headCellStyles}>
                      {sortLabel("createdAt", "Created")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((row) => {
                    const isNew = (row.status || "new") === "new";
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleOpenRow(row)}
                      >
                        <TableCell
                          padding="checkbox"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={selectedIds.includes(row.id)}
                            onChange={() => toggleRow(row.id)}
                          />
                        </TableCell>
                        <TableCell sx={cellStyles}>
                          <Chip
                            size="small"
                            color={isNew ? "primary" : "default"}
                            label={row.status || "new"}
                          />
                        </TableCell>
                        <TableCell sx={{ ...cellStyles, ...linkStyles }}>
                          {row.organization || "—"}
                        </TableCell>
                        <TableCell sx={cellStyles}>
                          {row.firstName || "—"}
                        </TableCell>
                        <TableCell sx={cellStyles}>
                          {row.lastName || "—"}
                        </TableCell>
                        <TableCell sx={cellStyles}>{row.email || "—"}</TableCell>
                        <TableCell sx={cellStyles}>{row.phone || "—"}</TableCell>
                        <TableCell sx={cellStyles}>
                          {formatDate(row.createdAt)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
      </Grid>

      <ContactSubmissionModal
        submission={activeRow}
        open={!!activeRow}
        onClose={() => setActiveRow(null)}
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default ContactSubmissionsContainer;

const searchStyles = {
  height: 40,
  borderRadius: 5,
  width: 320,
  background: "#7676801F",
  color: "#515563",
  fontSize: 14,
  fontFamily: "Avenir Book, sans-serif",
};

const selectedTextStyles = {
  color: "#020303",
  marginLeft: 16,
  marginRight: 10,
  fontSize: 14,
  fontWeight: 500,
  fontFamily: "Avenir Book, sans-serif",
};

const actionTextStyles = {
  color: "#00A7CD",
  marginLeft: 10,
  marginRight: 10,
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "Avenir Book, sans-serif",
};

const headCellStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 700,
  fontSize: 13,
  color: "#020303",
};

const cellStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 14,
  color: "#020303",
};

const linkStyles = {
  color: "#00A7CD",
  fontWeight: 600,
};

const emptyStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 14,
  color: "#808080",
  p: 2,
};
